import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ajvResolver,
  apiAbortController,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
} from '@ui/src';
import {
  LinkedClientsFormValues,
  PspIntegration,
  PspIntegrationOrganization,
  PspIntegrationPayment,
  PspPartner,
} from '../../types';
import {
  useBindAccountsExternalOrganizationMutation,
  useLazyGetAccountsLinkedClientsQuery,
  useLazyGetAccountsPspIntegrationsOrganizationsByNameQuery,
} from '../../../accounts/store/services';
import useGetOrganizations from '../../helpers/use-get-organizations';

import ROUTES from '../../../../routes';

const schema = {
  type: 'object',
  properties: {
    externalId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'External Customer is required',
    },
    organizationId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Customer is required',
    },
  },
  required: ['externalId', 'organizationId'],
};

const useLinkedClientsForm = ({
  linkedClientsTakeCount,
  search,
  pspPartners,
  pspIntegrations,
}: {
  search?: string;
  linkedClientsTakeCount?: number;
  pspPartners?: PspPartner[];
  pspIntegrations?: PspIntegration[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<
    SelectItemProps | undefined
  >(undefined);
  const [selectedPspIntegrationId, setSelectedPspIntegrationId] = useState('');
  const [availablePspPartners, setAvailablePspPartners] = useState<
    SelectItemProps[]
  >([]);

  const [
    externalOrganizationsListVisible,
    setExternalOrganizationsListVisible,
  ] = useState(false);
  const [externalOrganizationSearch, setExternalOrganizationSearch] =
    useState('');
  const [pspIntegrationOrganizationsList, setPspIntegrationOrganizationsList] =
    useState<PspIntegrationOrganization[]>([]);

  const [getLinkedClients] = useLazyGetAccountsLinkedClientsQuery();
  const [
    getPspIntegrationOrganizations,
    { isLoading: organizationsIsLoading, isFetching: organizationsIsFetching },
  ] = useLazyGetAccountsPspIntegrationsOrganizationsByNameQuery();

  const [
    bindExternalOrganization,
    { isLoading: requestInProgress, isSuccess },
  ] = useBindAccountsExternalOrganizationMutation();

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
    watch,
  } = useForm<LinkedClientsFormValues>({
    resolver: ajvResolver(schema),
  });

  const pspPartnerId = watch('pspPartnerId');

  const { autocompleteList, onSearch, isLoading, onClear } =
    useGetOrganizations();

  const getPspIntegrationOrganizationsAction = async ({
    name,
  }: {
    name: string;
  }) => {
    apiAbortController.abortPendingRequest();

    const response = await getPspIntegrationOrganizations(
      `?pspIntegrationId=${selectedPspIntegrationId}&name=${name}&take=40`,
    );

    if (response?.data?.data) {
      const nextOrganizationsList = response?.data?.data;

      setPspIntegrationOrganizationsList(nextOrganizationsList);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await bindExternalOrganization({
      organizationId: values.organizationId,
      organizationType:
        selectedOrganization?.description === 'Individual'
          ? 'INDIVIDUAL'
          : 'CORPORATE',
      externalId: values?.externalId,
      active: true,
      pspPartnerId: values.pspPartnerId,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');
    setSelectedOrganization(undefined);
    setSelectedPspIntegrationId('');

    setTimeout(() => {
      navigate(`${ROUTES.EXTERNAL_ACCOUNTS_LINKED_CLIENTS}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (
      externalOrganizationSearch &&
      (organizationsIsLoading || organizationsIsFetching)
    ) {
      setExternalOrganizationsListVisible(true);
    }
  }, [
    externalOrganizationSearch,
    organizationsIsLoading,
    organizationsIsFetching,
  ]);

  useEffect(() => {
    if (pspPartnerId) {
      const currentPartner = pspPartners?.find(
        (item) => item.id === pspPartnerId,
      );

      if (currentPartner) {
        setSelectedPspIntegrationId(currentPartner?.pspIntegrationId as string);
      }
    }
  }, [pspPartnerId, pspPartners]);

  useEffect(() => {
    if (pspPartners && pspIntegrations) {
      const nextPspPartners = pspPartners.map((item) => {
        const result = { ...item };

        if (
          result.pspIntegrationId &&
          pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          )
        ) {
          const currentItegration = pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          );

          if (currentItegration) {
            result.pspIntegration = {
              name: currentItegration.name,
              pspIntegrationPayments:
                currentItegration.pspIntegrationPayments as PspIntegrationPayment[],
              id: currentItegration.id,
              externalCustomersSupported:
                currentItegration.externalCustomersSupported,
            };
          }
        }

        return result;
      });

      const pspPartnersWithExternalCustomerSupport = nextPspPartners?.filter(
        (item) => item?.pspIntegration?.externalCustomersSupported,
      );

      if (pspPartnersWithExternalCustomerSupport?.length) {
        setAvailablePspPartners(
          pspPartnersWithExternalCustomerSupport?.map((item) => ({
            name: item.companyName,
            value: item.id,
          })),
        );
      }
    }
  }, [pspPartners, pspIntegrations]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();

      getLinkedClients(
        `?take=${linkedClientsTakeCount}${search ? search.replace('?', '&') : ''}`,
      );
    }
  }, [isSuccess]);

  return {
    panelIsOpen,
    isDirty,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    requestInProgress,
    onSubmit,
    dirtyPopupVisible,
    control,
    availablePspPartners,
    pspPartnerId,
    errors,
    autocompleteList,
    isLoading,
    onSearch,
    setSelectedOrganization,
    onClear,
    setExternalOrganizationsListVisible,
    externalOrganizationSearch,
    setExternalOrganizationSearch,
    getPspIntegrationOrganizationsAction,
    pspIntegrationOrganizationsList,
    externalOrganizationsListVisible,
    organizationsIsLoading,
    organizationsIsFetching,
  };
};

export default useLinkedClientsForm;
